import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';

interface Option {
  value: string | number;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value: Option | null;
  onChange: (option: Option | null) => void;
  placeholder?: string;
  label?: string;
  isSearchable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  className?: string;
  fullWidth?: boolean;
  inMobileSheet?: boolean;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'انتخاب کنید',
  label,
  isSearchable = false,
  isDisabled = false,
  isLoading = false,
  className = '',
  fullWidth = false,
  inMobileSheet = false,
  onSearchChange,
  searchPlaceholder = 'جستجو',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 240 });
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const updateSearchValue = useCallback(
    (nextValue: string) => {
      setSearchValue(nextValue);
      onSearchChange?.(nextValue);
    },
    [onSearchChange],
  );

  // Filter options based on search
  const filteredOptions = searchValue
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
      )
    : options;

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobileLayout(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (!selectRef.current) return;
    const rect = selectRef.current.getBoundingClientRect();
    const viewportOffsetTop = window.visualViewport?.offsetTop ?? 0;
    const viewportOffsetLeft = window.visualViewport?.offsetLeft ?? 0;
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const menuMaxHeight = inMobileSheet ? 320 : 280;
    const edgePadding = 12;

    if (fullWidth || isMobileLayout) {
      const horizontalPadding = inMobileSheet ? 16 : 16;
      const menuWidth = Math.min(viewportWidth - horizontalPadding * 2, 480);
      const spaceBelow = viewportHeight - rect.bottom - edgePadding;
      const spaceAbove = rect.top - edgePadding;
      const openUpward = spaceBelow < Math.min(menuMaxHeight, 220) && spaceAbove > spaceBelow;
      const availableHeight = Math.max(
        160,
        Math.min(menuMaxHeight, openUpward ? spaceAbove : spaceBelow)
      );

      setDropdownPosition({
        top: openUpward
          ? rect.top + viewportOffsetTop - availableHeight - 4
          : rect.bottom + viewportOffsetTop + 4,
        left: viewportOffsetLeft + (viewportWidth - menuWidth) / 2,
        width: menuWidth,
        maxHeight: availableHeight,
      });
      return;
    }

    const menuWidth = Math.max(320, rect.width);
    const spaceBelow = viewportHeight - rect.bottom - edgePadding;
    const spaceAbove = rect.top - edgePadding;
    const openUpward = spaceBelow < menuMaxHeight && spaceAbove > spaceBelow;
    const availableHeight = Math.max(
      160,
      Math.min(menuMaxHeight, openUpward ? spaceAbove : spaceBelow)
    );

    setDropdownPosition({
      top: openUpward
        ? rect.top + viewportOffsetTop - availableHeight - 4
        : rect.bottom + viewportOffsetTop + 4,
      left: rect.right - menuWidth + viewportOffsetLeft,
      width: menuWidth,
      maxHeight: availableHeight,
    });
  }, [fullWidth, inMobileSheet, isMobileLayout]);

  // Close dropdown when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const isClickInsideSelect = selectRef.current && selectRef.current.contains(target);
      const isClickInsideDropdown = target && (target as HTMLElement).closest('.select-menu');
      
      if (!isClickInsideSelect && !isClickInsideDropdown) {
        setIsOpen(false);
        updateSearchValue('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isOpen, updateSearchValue]);

  // Focus search input when dropdown opens and calculate position
  useEffect(() => {
    if (isOpen) {
      if (isSearchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
      updateDropdownPosition();

      const viewport = window.visualViewport;
      const handleViewportChange = () => updateDropdownPosition();
      viewport?.addEventListener('resize', handleViewportChange);
      viewport?.addEventListener('scroll', handleViewportChange);
      window.addEventListener('resize', handleViewportChange);
      return () => {
        viewport?.removeEventListener('resize', handleViewportChange);
        viewport?.removeEventListener('scroll', handleViewportChange);
        window.removeEventListener('resize', handleViewportChange);
      };
    }
  }, [isOpen, isSearchable, updateDropdownPosition]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
          event.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          updateSearchValue('');
          setHighlightedIndex(-1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            handleOptionClick(filteredOptions[highlightedIndex]);
          }
          break;
      }
    },
    [isOpen, filteredOptions, highlightedIndex]
  );

  // Scroll to highlighted option
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current) {
      const highlightedElement = optionsRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  const handleOptionClick = (option: Option) => {
    onChange(option);
    setIsOpen(false);
    updateSearchValue('');
    setHighlightedIndex(-1);
  };

  const toggleDropdown = () => {
    if (!isDisabled && !isLoading) {
      setIsOpen(!isOpen);
      if (isOpen) {
        updateSearchValue('');
        setHighlightedIndex(-1);
      }
    }
  };

  const inlineMenuMaxHeight = inMobileSheet ? 240 : dropdownPosition.maxHeight;

  const optionsList = (
    <div
      ref={optionsRef}
      role="listbox"
      style={{
        maxHeight: `${Math.max(120, inlineMenuMaxHeight - (isSearchable ? 72 : 16))}px`,
        padding: '8px',
        overflowY: 'auto',
        direction: 'ltr',
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--border-muted) transparent',
        WebkitOverflowScrolling: 'touch',
      }}
      className="custom-scrollbar"
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, index) => (
          <div
            key={option.value}
            role="option"
            aria-selected={value?.value === option.value}
            onClick={() => handleOptionClick(option)}
            onMouseEnter={() => setHighlightedIndex(index)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: 'row',
              direction: 'rtl',
              padding: '10px 6px',
              margin: '2px 4px',
              borderRadius: 'var(--button-border-radius)',
              cursor: 'pointer',
              transition: 'all 0.15s ease-in-out',
              backgroundColor:
                highlightedIndex === index
                  ? 'var(--surface-secondary)'
                  : 'transparent',
              minHeight: '36px',
            }}
          >
            <div
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: `2px solid ${
                  value?.value === option.value
                    ? 'var(--color-primary-500)'
                    : 'var(--border-muted)'
                }`,
                backgroundColor:
                  value?.value === option.value
                    ? 'var(--color-primary-500)'
                    : 'transparent',
                marginRight: '12px',
                flexShrink: 0,
                position: 'relative',
                marginLeft:'12px',
                transition: 'all 0.2s ease',
              }}
            >
              {value?.value === option.value && (
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </div>
            <span
              style={{
                color: 'var(--content-primary)',
                fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
                flex: 1,
                lineHeight: '1.5',
                textAlign: 'right',
                marginLeft: '12px',
              }}
            >
              {option.label}
            </span>
          </div>
        ))
      ) : (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: 'var(--content-tertiary)',
            fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
          }}
        >
          موردی یافت نشد
        </div>
      )}
    </div>
  );

  const searchHeader = isSearchable ? (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: 'var(--surface-secondary)',
        borderBottom: '1px solid var(--border-muted)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
      }}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'var(--content-tertiary)',
            zIndex: 1,
          }}
        >
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" />
          <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => {
            updateSearchValue(e.target.value);
            setHighlightedIndex(-1);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            padding: '8px 12px 8px 36px',
            border: '1px solid var(--border-focus)',
            borderRadius: 'var(--button-border-radius)',
            fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
            backgroundColor: 'var(--surface-primary)',
            color: 'var(--content-primary)',
            outline: 'none',
            fontFamily: 'inherit',
            direction: 'rtl',
            textAlign: 'right',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s ease',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-focus)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-muted)';
          }}
        />
      </div>
    </div>
  ) : null;

  const dropdownMenu = (
    <div
      className={`select-menu ${inMobileSheet ? 'select-menu--inline' : ''}`}
      style={
        inMobileSheet
          ? {
              backgroundColor: 'var(--surface-elevated)',
              border: '1px solid var(--border-muted)',
              borderRadius: '12px',
              overflow: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }
          : {
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              backgroundColor: 'var(--surface-elevated)',
              border: '1px solid var(--border-muted)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              zIndex: 9999999,
              width: `${dropdownPosition.width}px`,
              minWidth: fullWidth || isMobileLayout ? `${dropdownPosition.width}px` : '320px',
              maxWidth: '95vw',
              maxHeight: `${dropdownPosition.maxHeight}px`,
              overflow: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }
      }
    >
      {searchHeader}
      {optionsList}
    </div>
  );

  return (
    <div 
      ref={selectRef}
      className={`custom-select ${isOpen ? 'is-open' : ''} ${className}`}
      style={{ position: 'relative' }}
    >
      {/* Select Control */}
      <div
        className="select-control"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={isDisabled}
        style={{
          backgroundColor: isDisabled ? 'var(--surface-muted)' : 'var(--surface-secondary)',
          borderColor: isOpen ? 'var(--border-interactive)' : 'var(--border-muted)',
          borderRadius: 'var(--button-border-radius)',
          borderWidth: '1px',
          borderStyle: 'solid',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          width: fullWidth ? '100%' : '200px',
          minWidth: fullWidth ? '0' : '200px',
          maxWidth: fullWidth ? '100%' : '220px',
          height: '36px',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          transition: 'all 0.2s ease-in-out',
          opacity: isDisabled ? 0.6 : 1,
        }}
      >
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center',
          overflow: 'hidden',
        }}>
          {value ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              color: 'var(--content-primary)', 
              fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`, 
              fontWeight: '500',
              gap: '6px',
            }}>
              {label && (
                <>
                  <span style={{ color: 'var(--content-secondary)' }}>{label}</span>
                  <span style={{ color: 'var(--content-secondary)' }}>|</span>
                </>
              )}
              <span style={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>{value.label}</span>
            </div>
          ) : (
            <span style={{ 
              color: 'var(--content-placeholder)', 
              fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
              fontWeight: '400',
            }}>
              {placeholder}
            </span>
          )}
        </div>
        
        {/* Loading Indicator */}
        {isLoading && (
          <div style={{
            width: '16px',
            height: '16px',
            border: '2px solid var(--border-muted)',
            borderTopColor: 'var(--color-primary-500)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
        )}
        
        {/* Dropdown Indicator */}
        {!isLoading && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              color: 'var(--content-secondary)',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && !isDisabled && (
        inMobileSheet ? (
          dropdownMenu
        ) : (
          ReactDOM.createPortal(
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 9999998,
                  backgroundColor: 'transparent',
                }}
                onClick={() => {
                  setIsOpen(false);
                  updateSearchValue('');
                  setHighlightedIndex(-1);
                }}
              />
              {dropdownMenu}
            </>,
            document.body
          )
        )
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--border-muted);
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: var(--content-tertiary);
        }
      `}</style>
    </div>
  );
};

export default CustomSelect;